/**
 * Page furniture: header, footer, and the small pieces every page reuses.
 *
 * ── The one thing not in the header ─────────────────────────────────────────
 * There is no sign-in link and no account menu, anywhere, for anyone. That used
 * to be a product decision holding back a developer area that lived on this
 * origin; it is now simply a description — this site has no accounts, and a
 * link to sign in would lead nowhere.
 *
 * It is recorded here because it is the kind of thing a later change would
 * quietly undo. The console is at internal.mauvely.com, behind Cloudflare
 * Access, and a visitor has no business being offered a door they cannot open.
 */

import React from 'react';
import { ds } from '../ds';
import { Link } from '../router';
import { useApp } from '../state';
import { display, monoPlain, PAGE_WIDTH, RADIUS, tableHead } from '../theme';
import type { Tone } from '../status';

import { useT } from '../i18n';
import { LocaleSwitch } from './LocaleSwitch';
export function Page({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      <Header />
      <main
        style={{
          maxWidth: PAGE_WIDTH,
          margin: '0 auto',
          padding: '26px 20px 60px',
          display: 'grid',
          gap: 20,
          alignContent: 'start'
        }}
      >
        {children}
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  const t = useT();
  const { theme, setTheme, refreshing, summary } = useApp();
  const { Icon, IconButton } = ds();

  return (
    <header
      style={{
        borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--surface-card)'
      }}
    >
      <div
        style={{
          maxWidth: PAGE_WIDTH,
          margin: '0 auto',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16
        }}
      >
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'baseline', gap: 7 }}>
          <span
            style={{
              fontFamily: "var(--font-display), 'Baloo 2', system-ui",
              fontWeight: 800,
              fontSize: 21,
              color: 'var(--text-strong)'
            }}
          >
            mauvely<span style={{ color: 'var(--signal)' }}>.</span>
          </span>
          <span
            style={{
              fontFamily: "var(--font-display), 'Baloo 2', system-ui",
              fontWeight: 400,
              fontSize: 19,
              color: 'var(--text-muted)'
            }}
          >
            status
          </span>
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* A quiet "checking…" rather than a spinner that blanks the board.
              A status page that flickers every thirty seconds is unreadable. */}
          <span
            style={{
              ...monoPlain,
              fontSize: 10.5,
              opacity: refreshing ? 1 : 0,
              transition: 'opacity 200ms'
            }}
            aria-live="polite"
          >
            {t('chrome.checking')}
          </span>
          <a
            href={summary?.page.homeUrl ?? 'https://mauvely.com'}
            style={{ ...monoPlain, fontSize: 11.5, textDecoration: 'none' }}
            className="st-hide-narrow"
          >
            mauvely.com
          </a>
          <IconButton
            aria-label={t(theme === 'dark' ? 'chrome.theme.light' : 'chrome.theme.dark')}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} />
          </IconButton>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const t = useT();
  return (
    <footer
      style={{
        maxWidth: PAGE_WIDTH,
        margin: '0 auto',
        padding: '0 20px 44px',
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        justifyContent: 'space-between'
      }}
      className="st-no-print"
    >
      <span style={monoPlain}>{t('chrome.footer.maintenance')}</span>
      <span style={{ display: 'flex', gap: 16 }}>
        <Link to="/incidents" style={{ ...monoPlain, textDecoration: 'none' }}>
          {t('chrome.footer.incidents')}
        </Link>
        <a href="https://mauvely.com" style={{ ...monoPlain, textDecoration: 'none' }}>
          Mauvely
        </a>
        <LocaleSwitch />
      </span>
    </footer>
  );
}

// ── shared pieces ───────────────────────────────────────────────────────────

export const Panel = ({
  children,
  style
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <section
    style={{
      background: 'var(--surface-card)',
      borderRadius: RADIUS.panel,
      boxShadow: '0 1px 0 rgba(40,28,45,.04)',
      padding: 24,
      ...style
    }}
  >
    {children}
  </section>
);

export const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div style={tableHead}>{children}</div>
);

export const PageTitle = ({ title, meta }: { title: string; meta?: string }) => (
  <div style={{ display: 'grid', gap: 4 }}>
    <h1 style={display(30)}>{title}</h1>
    {meta && <p style={{ ...monoPlain, margin: 0 }}>{meta}</p>}
  </div>
);

/**
 * A state pill: colour, icon and word, always all three.
 *
 * This component is the enforcement point for the rule the palette depends on.
 * Every caller passes a `Tone`, and every `Tone` in status.ts carries an icon
 * and a label — so there is no way to render a state here and ship only the
 * colour. Warning sits below 3:1 on the light surface by design; the word
 * beside it is what makes that acceptable rather than a bug.
 */
export function StatePill({ tone, size = 'md' }: { tone: Tone; size?: 'sm' | 'md' }) {
  const { Icon } = ds();
  const small = size === 'sm';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: small ? 5 : 6,
        background: tone.soft,
        color: tone.ink,
        borderRadius: RADIUS.pill,
        padding: small ? '2px 9px' : '4px 12px',
        fontSize: small ? 11.5 : 13,
        fontWeight: 600,
        whiteSpace: 'nowrap'
      }}
    >
      <Icon name={tone.icon} size={small ? 12 : 14} />
      {tone.label}
    </span>
  );
}

export const Footnote = ({ children }: { children: React.ReactNode }) => (
  <p style={{ ...monoPlain, margin: 0 }}>{children}</p>
);
